import React, { ChangeEvent } from 'react';
import { Autocomplete, TextField, Grid, Typography, Switch, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Form, FormikProps, useFormikContext, Field } from 'formik';
import { useSelector } from 'react-redux';
import { EvalStatuses, EvalStatusLabels } from '../../enums/EvalStatuses';
import { EvalFilterFields } from '../../enums/EvalFilterFields';
import { EopStatuses, EopStatusLabels } from '../../enums/EopStatuses';
import { useStyles } from './MyPeopleEvalFiltersForm.styles';
import { rockySwitcherStyles } from '@styles/rockySwitcherStyles';
import { PeopleSearchSelector } from '@shared/components/PeopleSearchSelector/PeopleSearchSelector';
import { LocationSelector } from '@shared/components/LocationSelector/LocationSelector';
import { DepartmentSelector } from '@shared/components/DepartmentSelector/DepartmentSelector';
import { DepartmentData } from '@shared/interfaces/DepartmentData';
import { LocationOptionData } from '@shared/interfaces/LocationOptionData';
import { EvalFiltersFields } from '../../interfaces/EvalFiltersFields';
import { useEvalHandlers } from '../../hooks/useEvalHandlers';
import { DateRangeFilter } from '@shared/components/DateRangeFilter/DateRangeFilter';
import { evalDateRangeList } from '@shared/consts/dateRange';
import { EmploymentType } from '@shared/enums/EmploymentType';
import { userProfileOfficeCountrySelector } from '@modules/App/pages/UserProfilePage/redux/selectors';
import { getEvaluationTerms } from '@shared/helpers/evaluationTerminology';
import { EngagementModel, EngagementModelLabels } from '@modules/EvaluationModule/enums/EngagementModel';

interface Props {
  departments: DepartmentData[];
  locations: LocationOptionData[];
}

export const MyPeopleEvalFiltersForm: React.FC<Props> = ({ departments, locations }) => {
  const styles = useStyles();
  const rockySwitcher = rockySwitcherStyles();
  const formik: FormikProps<EvalFiltersFields> = useFormikContext();
  const officeCountry = useSelector(userProfileOfficeCountrySelector);
  const evaluationTerms = getEvaluationTerms(officeCountry);

  const {
    handleStatusChange,
    handleLocationChange,
    handleDepartmentsChange,
    handlePeopleSelectorChange,
    handleIncludeRegularChange,
    handleIncludeEopChange,
    handleEopStatusRadioChange,
    handleRescheduledChange,
    handleDatesChange,
    handleEmploymentTypeChange,
    handleHasOngoingFlagChange,
    handleEngagementModelsChange,
  } = useEvalHandlers(departments, locations);

  const selectedLocations = (locations ?? []).filter((option) =>
    formik.values.locations.some((x) => x === option.value)
  );

  return (
    <Form>
      <Grid container spacing={2} className={styles.drawlerRoot}>
        <Grid item xs={12}>
          <DepartmentSelector
            name={EvalFilterFields.Departments}
            placeholder="Any project"
            options={departments}
            value={formik.values.departments || []}
            onChange={handleDepartmentsChange}
          />
        </Grid>
        <Grid item xs={12}>
          <LocationSelector
            name={EvalFilterFields.Locations}
            placeholder="Any location"
            options={locations}
            value={selectedLocations}
            onChange={handleLocationChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                padding: '10px',
              },
            }}
            options={Object.values(EmploymentType)}
            value={formik.values.employmentType}
            onChange={(_, newValue) => handleEmploymentTypeChange(EvalFilterFields.EmploymentType, newValue)}
            renderInput={(params) => <TextField {...params} label="Employment type" placeholder="Any type" />}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                padding: '10px',
              },
            }}
            options={Object.values(EngagementModel)}
            value={formik.values.engagementModels || []}
            onChange={(_, newValue) => handleEngagementModelsChange(EvalFilterFields.EngagementModels, newValue)}
            getOptionLabel={(option) => EngagementModelLabels[option as EngagementModel] || option}
            renderInput={(params) => <TextField {...params} label="Engagement model" placeholder="Any model" />}
          />
        </Grid>
        <Grid item xs={12}>
          <DateRangeFilter
            label="Date"
            fromField={EvalFilterFields.DateFrom}
            toField={EvalFilterFields.DateTo}
            fromValue={formik.values.dateFrom}
            toValue={formik.values.dateTo}
            onChange={handleDatesChange}
            options={evalDateRangeList}
          />
        </Grid>
        <Grid item xs={12} className={styles.connectedGrid}>
          <FormControlLabel
            className={styles.formControlLabel}
            control={
              <Switch
                className={rockySwitcher.switcher}
                checked={formik.values.isRescheduled}
                onChange={(e) => handleRescheduledChange(EvalFilterFields.IsRescheduled, e.target.checked)}
                disableRipple
              />
            }
            label={<Typography variant="subtitle1">Has been rescheduled</Typography>}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                padding: '10px',
              },
            }}
            options={Object.values(EvalStatuses)}
            value={formik.values.status || []}
            onChange={(_, newValue) => handleStatusChange(EvalFilterFields.Status, newValue)}
            getOptionLabel={(option) => EvalStatusLabels[option as EvalStatuses] || option}
            renderInput={(params) => <TextField {...params} label="Status" placeholder="Any status" />}
          />
        </Grid>
        <Grid item xs={12} className={styles.connectedGrid}>
          <FormControlLabel
            className={styles.formControlLabel}
            control={
              <Switch
                className={rockySwitcher.switcher}
                checked={formik.values.includeRegular}
                onChange={(e) => handleIncludeRegularChange(EvalFilterFields.IncludeRegular, e.target.checked)}
                disableRipple
              />
            }
            label={<Typography variant="subtitle1">Regular evaluations</Typography>}
          />
          <FormControlLabel
            className={styles.formControlLabel}
            control={
              <Switch
                className={rockySwitcher.switcher}
                checked={formik.values.includeEop}
                onChange={(e) => handleIncludeEopChange(EvalFilterFields.IncludeEop, e.target.checked)}
                disableRipple
              />
            }
            label={<Typography variant="subtitle1">{evaluationTerms.endOfPeriod}</Typography>}
          />
        </Grid>
        {formik.values.includeEop && (
          <Grid item xs={12} className={styles.radioGroupLabel}>
            <Field
              name={EvalFilterFields.EopStatus}
              as={RadioGroup}
              row
              className={styles.radioGroup}
              value={formik.values.eopStatus || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleEopStatusRadioChange(EvalFilterFields.EopStatus, e.target.value)
              }
            >
              {Object.values(EopStatuses).map((status) => (
                <FormControlLabel
                  key={status}
                  value={status}
                  control={
                    <Radio
                      onClick={() => {
                        if (formik.values.eopStatus === status) {
                          handleEopStatusRadioChange(EvalFilterFields.EopStatus, '');
                        }
                      }}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={EopStatusLabels[status as EopStatuses]}
                />
              ))}
            </Field>
          </Grid>
        )}
        {formik.values.includeEop && (
          <Grid item xs={12}>
            <DateRangeFilter
              label="Resolved date"
              fromField={EvalFilterFields.EopResolvedAtFrom}
              toField={EvalFilterFields.EopResolvedAtTo}
              fromValue={formik.values.eopResolvedAtFrom}
              toValue={formik.values.eopResolvedAtTo}
              onChange={handleDatesChange}
              options={evalDateRangeList}
            />
          </Grid>
        )}
        {formik.values.includeEop && (
          <Grid item xs={12}>
            <DateRangeFilter
              label="Extended to date"
              fromField={EvalFilterFields.EopExtendedToFrom}
              toField={EvalFilterFields.EopExtendedToTo}
              fromValue={formik.values.eopExtendedToFrom}
              toValue={formik.values.eopExtendedToTo}
              onChange={handleDatesChange}
              options={evalDateRangeList}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControlLabel
            className={styles.formControlLabel}
            control={
              <Switch
                className={rockySwitcher.switcher}
                checked={formik.values.hasOngoingFlag}
                onChange={(e) => handleHasOngoingFlagChange(EvalFilterFields.HasOngoingFlag, e.target.checked)}
                disableRipple
              />
            }
            label={<Typography variant="subtitle1">Has ongoing flag</Typography>}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={styles.chapterTitle}>
            Participants
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <PeopleSearchSelector
            title="Manager"
            name={EvalFilterFields.Managers}
            placeholder="Any manager"
            value={formik.values.managers}
            onChange={handlePeopleSelectorChange}
          />
        </Grid>
        <Grid item xs={12}>
          <PeopleSearchSelector
            title="Evaluation for"
            name={EvalFilterFields.Members}
            placeholder="Anyone"
            value={formik.values.members}
            onChange={handlePeopleSelectorChange}
          />
        </Grid>
      </Grid>
    </Form>
  );
};
